import { translateOne } from './src/translateOne';
import { translateFallback } from './src/translateFallback';

/**
 * Devuelve la cadena traducida y interpolada si tiene variables.
 *
 * Cada `key` puede ser un {string} o un {Object}:
 * --------------------------------------------------
 * `keys` es un `key` o una colección de `key`,
 * y cada uno individualmente puede ser
 * - un {string}
 * @example 'global.foo'
 *
 * - un {Object<string, object>}
 * @example {'global.foo': { foo: 'bar' } }
 *
 * # `keys` puede ser un string o un array:
 * -----------------------------------------
 * - Si `keys` es un string, devolvera un string:
 * @example edicom.locale.translate('global.foo') // -> 'bar'
 *
 * - Si `keys` es un array, devolvera un objeto:
 * @example
 * edicom.locale.translate([
 *   'global.foo', // -> 'bar'
 *   { 'global.min': {min: 3} }, // -> 'Minimo 3'
 * ]) // -> { 'global.foo': 'bar', 'global.min': 'Minimo 3' }
 *
 *
 * @param {Object} state
 * @param {string} localeId la locale/idioma destino a traducir
 * @param {string|string[]|Object[]} keys la clave de la cadena, o un array de claves
 * @param {Object} [params] el objeto con los valores de las variables a interpolar
 * @param {string} [defaultValue] el valor por defecto si "Error not found" sino especificado, devuelve el `key` buscado
 * @returns {string|object<key: value>}
 */
export function translateTo(state, localeId, keys, params, defaultValue) {
  const dictionary = getDictionary(state, localeId);

  if (typeof params === 'string' && typeof defaultValue === 'undefined') {
    defaultValue = params;
    params = null;
  }

  if (typeof keys === 'string') {
    return translateOne(dictionary, keys, params, defaultValue)?.[keys] ?? translateFallback(state, keys, params, defaultValue)?.[keys] ?? keys;
  }

  if (typeof keys === 'object' && Array.isArray(keys)) {
    return keys.reduce((acc, cur) => {
      const traduction = translateOne(dictionary, cur, params, defaultValue) ?? translateFallback(state, cur, params, defaultValue) ?? cur;
      const key = Object.keys(traduction)[0];
      acc[key] = traduction[key];
      return acc;
    }, {});
  }

  throw new Error(`edicom.locale.translate: You are trying to translate invalid keys: "${keys}", it must be a string o string[] o Object[]. Found type "${typeof keys}".`);
}

function getDictionary(state, localeId = '') {
  // recuperamos el dicionario en función de la localeId ej. "es", "en-US", "pt-BR"
  // sino, en función del idioma "es", "en",...

  const dictionary = state.dictionary[localeId] ?? state.dictionary[localeId.substring(0, 2)];
  if (!dictionary) {
    throw new Error(`edicom.locale.translate: "dictionary" not loaded for locale "${localeId}". Load the dictionary with "edicom.locale.translations('${localeId}', keyValues)"`);
  }

  return dictionary;
}
